import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import FeatherIcon from "feather-icons-react";
import { DebounceInput } from "react-debounce-input";
import { colors } from "./colors";

const statusStyles = (variant) => ({
    normal: css`
        border: 1px solid ${colors.passive[300]};
        color: ${colors.passive[500]};
    `,
    focus: css`
        color: ${colors[variant][900]};
        border: 2px solid ${colors[variant][900]};
        padding: ${(props) => (props.size === "small" ? "8px 11px" : "10px 14px")};
    `,
    filled: css`
        color: ${colors[variant][900]};
    `,
    disabled: css`
        border-color: ${colors.neutral[300]};
        color: ${colors.neutral[300]};
        background-color: ${colors.neutral[25]};
    `,
    error: css`
        border-color: #e74c3c;
        color: #e74c3c;
    `,
});

const InputContainer = styled.div`
    background-color: ${colors.white};
    display: flex;
    align-items: center;
    border: 1px solid ${colors.neutral[300]};
    border-radius: 8px;
    padding: ${(props) => (props.size === "small" ? "8px 12px" : "10px 15px")};
    height: ${(props) => (props.size === "small" ? "36px" : "41px")};

    ${({ status, variant }) => statusStyles(variant)[status]}
`;

const StyledDebounceInput = styled(DebounceInput)`
    border: none;
    outline: none;
    flex: 1;
    font-size: ${(props) => (props.size === "small" ? "0.85rem" : "0.91563rem")};
    font-weight: 400;
    line-height: ${(props) => (props.size === "small" ? "1.125rem" : "1.2938rem")};

    background-color: transparent;
    width: 100%;
    color: ${colors.neutral[800]};
    svg {
    }
    &::placeholder {
        color: ${colors.neutral[500]};
    }

    &:disabled {
        border-color: ${colors.neutral[300]};
        color: ${colors.neutral[300]};
        background-color: ${colors.neutral[25]};

        &::placeholder {
            color: ${colors.neutral[300]};
        }
    }

    ${(props) =>
        props.type === "number" &&
        css`
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            -moz-appearance: textfield;
        `}
`;

const UnStyledDebounceInput = styled(DebounceInput)`
    border: none;
    outline: none;
    padding: 0;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    background-color: transparent;
    width: 100%;
    &:focus {
        text-decoration: underline;
    }
    ${({ error }) =>
        error &&
        css`
            text-decoration: underline;
            color: #e74c3c;
        `}
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    width: 20px;
    height: 20px;
`;

export const Seperator = styled.div`
    width: 1px;
    height: 100%;
    margin-left: 8px;
    margin-right: 10px;
    background-color: ${({ isDisabled }) => (isDisabled ? `${colors.neutral[500]}` : `${colors.neutral[300]}`)};
`;

const IconContainerForChildren = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    min-width: 12px;
    min-height: 12px;
    box-sizing: content-box;
`;

const InputWithIcon = ({
    leftIcon,
    rightIcon,
    leftIconClick,
    value,
    onChange,
    placeholder,
    rightIconClick,
    disabled,
    debounceTime = 1000,
    error = false,
    type,
    iconType,
    leftMaterialIcon,
    rightMaterialIcon,
    variant = "primary",
    onKeyDown,
    onPaste,
    min,
    minLength = 2,
    maxLength,
    inputMode,
    pattern,
    size,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);

    const handleKeyDown = (event) => {
        if (type === "number" && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
            event.preventDefault();
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    };

    const handleWheel = (event) => {
        if (type === "number") {
            event.target.blur();
        }
    };

    let status = "normal";

    if (disabled) {
        status = "disabled";
    } else if (error) {
        status = "error";
    } else if (isFocused) {
        status = "focus";
    } else if (value) {
        status = "filled";
    }
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && !inputRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    return variant !== "unstyled" ? (
        <InputContainer ref={inputRef} status={status} disabled={disabled} size={size} variant={variant}>
            {(leftIcon || leftMaterialIcon) && (
                <>
                    <IconContainer
                        aria-label="search-click"
                        data-testid="left-icon"
                        className="inputLeftIcon"
                        onClick={leftIconClick}
                    >
                        {iconType === "material" ? (
                            <span className="material-symbols-outlined">{leftMaterialIcon}</span>
                        ) : (
                            <FeatherIcon aria-label="search" icon={leftIcon} size={20} />
                        )}
                    </IconContainer>
                    <Seperator />
                </>
            )}
            <StyledDebounceInput
                minLength={minLength}
                debounceTimeout={debounceTime}
                value={value}
                type={type}
                min={min}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={handleKeyDown}
                onWheel={handleWheel}
                onPaste={onPaste}
                maxLength={maxLength}
                inputMode={inputMode}
                pattern={pattern}
                size={size}
            />

            {(rightIcon || rightMaterialIcon) && (
                <>
                    {typeof rightIcon === "string" || typeof rightMaterialIcon === "string" ? (
                        <IconContainer
                            aria-label="x-click"
                            data-testid="right-icon"
                            className="inputRightIcon"
                            onClick={rightIconClick}
                        >
                            {iconType === "material" && rightMaterialIcon ? (
                                <span className="material-symbols-outlined">{rightMaterialIcon}</span>
                            ) : (
                                <FeatherIcon aria-label="x" icon={rightIcon} size={20} />
                            )}
                        </IconContainer>
                    ) : (
                        <IconContainerForChildren
                            aria-label="x-click"
                            data-testid="right-icon"
                            className="inputRightIcon"
                        >
                            {rightIcon}
                        </IconContainerForChildren>
                    )}
                </>
            )}
        </InputContainer>
    ) : (
        <UnStyledDebounceInput
            error={error}
            debounceTimeout={debounceTime}
            value={value}
            type={type}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            onKeyDown={onKeyDown}
            {...props}
        />
    );
};

export default InputWithIcon;
